import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
  Fade,
  Slide
} from '@mui/material';
import {
  ArrowRight,
  Home,
  Building,
  Palette,
  Eye,
  Ruler,
  Star,
  Award,
  Users,
  Calendar,
  MapPin
} from 'lucide-react';

// Import actual images
import heroImage1 from '../assets/images/hero/hero-Image-1.png';
import heroImage2 from '../assets/images/hero/hero-Image-2.png';

// Temporary placeholder for missing images
const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iMCAwIDgwMCA2MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI4MDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik00MDAgMjAwTDQ4MCAzMDBINDUwVjM1MEgzNTBWMzAwSDMyMEw0MDAgMjAwWiIgZmlsbD0iI0ZGNTcyMiIgZmlsbC1vcGFjaXR5PSIwLjMiLz4KPHR5cGUgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4IiBmaWxsPSIjNjY2NjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj4KPHR5cGUgeD0iNDAwIiB5PSI0MDAiPkltYWdlIFBsYWNlaG9sZGVyPC90ZXh0Pgo8L3N2Zz4K';

const serviceImages = {
  interiorDesign: heroImage1,
  visualization: heroImage2,
  commercial: heroImage1,
  residential: heroImage2,
  spacePlanning: heroImage1
};

const projectImages = {
  featured1: heroImage1,
  featured2: heroImage2,
  featured3: heroImage1,
  featured4: heroImage2
};

const teamImages = {
  team1: heroImage1,
  team2: heroImage2,
  team3: heroImage1,
  team4: heroImage2
};

const testimonialImages = {
  client1: heroImage1,
  client2: heroImage2,
  client3: heroImage1
};

const getImageSrc = (imageSource) => imageSource || placeholderImage;

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showElements, setShowElements] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    {
      title: 'Interior Design',
      description: 'Transform your space into something extraordinary with our creative design solutions.',
      icon: Palette,
      image: serviceImages.interiorDesign,
      link: '/services/interior-design'
    },
    {
      title: '3D Visualization',
      description: 'See your vision come to life with photorealistic renderings and virtual tours.',
      icon: Eye,
      image: serviceImages.visualization,
      link: '/services/visualization'
    },
    {
      title: 'Commercial Design',
      description: 'Create professional spaces that enhance productivity and reflect your brand.',
      icon: Building,
      image: serviceImages.commercial,
      link: '/services/commercial-design'
    },
    {
      title: 'Residential Design',
      description: 'Design your dream home with personalized touches that reflect your lifestyle.',
      icon: Home,
      image: serviceImages.residential,
      link: '/services/residential-design'
    },
    {
      title: 'Space Planning',
      description: 'Optimize your space for functionality, flow, and maximum utilization.',
      icon: Ruler,
      image: serviceImages.spacePlanning,
      link: '/services/space-planning'
    }
  ];

  const featuredProjects = [
    {
      title: 'Modern Villa Design',
      category: 'Residential',
      location: 'Jubilee Hills, Hyderabad',
      year: '2024',
      image: projectImages.featured1,
      link: '/portfolio/residential'
    },
    {
      title: 'Corporate Headquarters',
      category: 'Commercial',
      location: 'Hitech City, Hyderabad',
      year: '2024',
      image: projectImages.featured2,
      link: '/portfolio/commercial'
    },
    {
      title: 'Luxury Hotel Suite',
      category: 'Hospitality',
      location: 'Banjara Hills, Hyderabad',
      year: '2023',
      image: projectImages.featured3,
      link: '/portfolio/hospitality'
    },
    {
      title: 'Community Library',
      category: 'Institutional',
      location: 'Gachibowli, Hyderabad',
      year: '2023',
      image: projectImages.featured4,
      link: '/portfolio/institutional'
    }
  ];

  const teamMembers = [
    {
      name: 'Priya Sharma',
      role: 'Founder & Creative Director',
      image: teamImages.team1,
      experience: '15+ Years'
    },
    {
      name: 'Rajesh Kumar',
      role: 'Senior Design Architect',
      image: teamImages.team2,
      experience: '12+ Years'
    },
    {
      name: 'Anjali Reddy',
      role: 'Interior Design Specialist',
      image: teamImages.team3,
      experience: '10+ Years'
    },
    {
      name: 'Vikram Singh',
      role: 'Project Manager',
      image: teamImages.team4,
      experience: '8+ Years'
    }
  ];

  const testimonials = [
    {
      name: 'Arjun Patel',
      role: 'Homeowner',
      image: testimonialImages.client1,
      content: 'The team transformed our house into a dream home. Every detail was perfectly executed.',
      rating: 5,
      project: 'Modern Family Villa'
    },
    {
      name: 'Sarah Johnson',
      role: 'Business Owner',
      image: testimonialImages.client2,
      content: 'Our office redesign has significantly improved team productivity and client impressions.',
      rating: 5,
      project: 'Corporate Office Design'
    },
    {
      name: 'Dr. Ramesh Gupta',
      role: 'Medical Practice',
      image: testimonialImages.client3,
      content: 'The clinic design creates a calming atmosphere that both staff and patients appreciate.',
      rating: 5,
      project: 'Healthcare Facility'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section - Enhanced Editorial Style */}
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 10, md: 0 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />

        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
          <Grid container sx={{ minHeight: '100vh' }}>
            {/* Left Side - Content */}
            <Grid 
              item 
              xs={12} 
              md={5} 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                pr: { md: 6 },
                position: 'relative'
              }}
            >
              <Fade in={showElements} timeout={1500}>
                <Box>
                  {/* Award Badge */}
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'rgba(255,87,34,0.08)',
                      px: 3,
                      py: 1,
                      mb: 4,
                      border: '1px solid rgba(255,87,34,0.2)'
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#FF5722',
                        mr: 2,
                        animation: 'pulse 2s ease-in-out infinite'
                      }}
                    />
                    <Typography 
                      variant="caption"
                      sx={{
                        color: '#FF5722',
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", sans-serif'
                      }}
                    >
                      Award-Winning Interior Design Studio
                    </Typography>
                  </Box>
                  
                  {/* Main Headline */}
                  <Typography 
                    variant="h1" 
                    component="h1"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      color: '#1A1A1A',
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      position: 'relative'
                    }}
                  >
                    WHERE DESIGN
                    <Box 
                      component="span" 
                      sx={{ 
                        display: 'block',
                        mt: 1,
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        position: 'relative'
                      }}
                    >
                      ELEGANCE
                    </Box>
                    <Box 
                      component="span" 
                      sx={{ 
                        display: 'block',
                        mt: 1,
                        fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                        fontWeight: 400,
                        letterSpacing: 2
                      }}
                    >
                      MEETS INNOVATION
                    </Box>
                  </Typography>
                  
                  {/* Enhanced Subtitle */}
                  <Typography 
                    variant="h6" 
                    component="p"
                    sx={{
                      color: '#666',
                      fontWeight: 400,
                      lineHeight: 1.7,
                      mb: 8,
                      maxWidth: 480,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    Transforming ordinary spaces into extraordinary experiences 
                    through innovative design, premium craftsmanship, and 
                    personalized attention to detail.
                  </Typography>

                  {/* Stats Section */}
                  <Box sx={{ display: 'flex', gap: 4, mb: 8 }}>
                    {[
                      { number: '500+', label: 'Projects Completed' },
                      { number: '8+', label: 'Years Experience' },
                      { number: '98%', label: 'Client Satisfaction' }
                    ].map((stat, index) => (
                      <Box key={index} sx={{ textAlign: 'left' }}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700,
                            color: '#FF5722',
                            mb: 0.5,
                            fontFamily: '"Inter", sans-serif'
                          }}
                        >
                          {stat.number}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#666',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontFamily: '"Inter", sans-serif'
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Enhanced CTA Buttons */}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      component={RouterLink}
                      to="/portfolio"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Explore Our Work
                    </Button>
                    
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      Start Your Project
                    </Button>
                  </Box>

                  {/* Trust Indicators */}
                  <Box sx={{ mt: 8, pt: 6, borderTop: '1px solid #E0E0E0' }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#999',
                        mb: 3,
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: 1.5,
                        fontWeight: 500,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      Trusted by Leading Brands
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', opacity: 0.6 }}>
                      {['Premium', 'Luxury', 'Corporate', 'Residential'].map((brand, index) => (
                        <Typography 
                          key={index}
                          variant="body2" 
                          sx={{ 
                            color: '#666',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            fontFamily: '"Inter", sans-serif'
                          }}
                        >
                          {brand}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Fade>

              {/* Enhanced Decorative Elements */}
              <Box
                sx={{
                  position: 'absolute',
                  left: -30,
                  top: '20%',
                  width: 4,
                  height: 120,
                  background: 'linear-gradient(to bottom, transparent, #FF5722, transparent)',
                  borderRadius: 2
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: -20,
                  top: '50%',
                  width: 2,
                  height: 80,
                  background: 'linear-gradient(to bottom, transparent, #FF8A65, transparent)',
                  borderRadius: 1
                }}
              />
            </Grid>

            {/* Right Side - Enhanced Hero Image */}
            <Grid 
              item 
              xs={12} 
              md={7}
              sx={{ 
                position: 'relative',
                minHeight: { xs: 400, md: '100vh' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Slide direction="left" in={showElements} timeout={1200}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '90%',
                    overflow: 'hidden',
                    borderRadius: 0,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
                  }}
                >
                  {/* Main Hero Image */}
                  <CardMedia
                    component="img"
                    image={getImageSrc(heroImage1)}
                    alt="Luxury Interior Design Showcase"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.8s ease',
                      '&:hover': {
                        transform: 'scale(1.03)'
                      }
                    }}
                  />

                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, transparent 50%, rgba(255,87,34,0.1) 100%)',
                      pointerEvents: 'none'
                    }}
                  />

                  {/* Enhanced Overlay Elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 30,
                      right: 30,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    {[1, 2, 3].map((dot) => (
                      <Box
                        key={dot}
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.9)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          animation: `pulse ${2 + dot * 0.5}s ease-in-out infinite`
                        }}
                      />
                    ))}
                  </Box>

                  {/* Enhanced Info Card */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 40,
                      right: 40,
                      background: 'rgba(255,255,255,0.98)',
                      backdropFilter: 'blur(20px)',
                      p: 4,
                      maxWidth: 320,
                      boxShadow: '0 15px 50px rgba(0,0,0,0.15)',
                      border: '1px solid rgba(255,255,255,0.8)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#FF5722',
                          mr: 2
                        }}
                      />
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          color: '#FF5722',
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          fontSize: '0.7rem',
                          fontFamily: '"Inter", sans-serif'
                        }}
                      >
                        Featured Project
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#1A1A1A',
                        fontWeight: 700,
                        mb: 2,
                        fontSize: '1.2rem',
                        lineHeight: 1.3,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      Luxury Modern Living
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                        mb: 3,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      A sophisticated blend of contemporary aesthetics and 
                      functional design, featuring premium materials and 
                      bespoke furniture solutions.
                    </Typography>
                    
                    <Button
                      size="small"
                      sx={{
                        color: '#FF5722',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        p: 0,
                        minWidth: 'auto',
                        fontFamily: '"Inter", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'transparent',
                          color: '#E64A19',
                          transform: 'translateX(4px)'
                        }
                      }}
                      endIcon={<ArrowRight size={16} />}
                    >
                      View Case Study
                    </Button>
                  </Box>

                  {/* Quality Indicator */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 40,
                      left: 40,
                      background: 'rgba(255,87,34,0.95)',
                      color: 'white',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      Premium Quality
                    </Typography>
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>

        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 0.7; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.2); }
            }
          `}
        </style>
      </Box>

      {/* Services Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: '#999',
                fontWeight: 500,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Our Services
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                color: '#1A1A1A',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Design Solutions for Every Space
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              sx={{ 
                color: '#666',
                maxWidth: 700,
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              From concept to completion, we offer comprehensive design services 
              tailored to your unique vision and needs.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={4} key={service.title}>
                <Card 
                  component={RouterLink}
                  to={service.link}
                  sx={{ 
                    height: '100%',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    background: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    borderRadius: 0,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      borderColor: '#FF5722'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', height: 250, overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={getImageSrc(service.image)}
                      alt={service.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        width: 50,
                        height: 50,
                        background: '#FF5722',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <service.icon size={24} color="white" />
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      sx={{ 
                        color: '#1A1A1A',
                        fontWeight: 600,
                        mb: 2,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      {service.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#FF5722' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          mr: 1,
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                          fontFamily: '"Inter", sans-serif'
                        }}
                      >
                        Learn More
                      </Typography>
                      <ArrowRight size={16} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Projects Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: '#999',
                fontWeight: 500,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Featured Projects
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                color: '#1A1A1A',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Our Latest Work
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              sx={{ 
                color: '#666',
                maxWidth: 700,
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Explore some of our recent projects that showcase our design expertise 
              across different spaces and styles.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {featuredProjects.map((project, index) => (
              <Grid item xs={12} sm={6} key={project.title}>
                <Card 
                  component={RouterLink}
                  to={project.link}
                  sx={{ 
                    borderRadius: 0,
                    overflow: 'hidden',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    height: 350,
                    background: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      '& .project-image': { transform: 'scale(1.05)' }
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', height: 250, overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={getImageSrc(project.image)}
                      alt={project.title}
                      className="project-image"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        background: 'rgba(255,255,255,0.9)',
                        px: 2,
                        py: 1
                      }}
                    >
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          color: '#FF5722',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          letterSpacing: 1,
                          fontFamily: '"Inter", sans-serif'
                        }}
                      >
                        {project.category}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ p: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#1A1A1A',
                        fontWeight: 600,
                        mb: 1,
                        fontFamily: '"Inter", sans-serif'
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#666' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MapPin size={14} />
                        <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif' }}>
                          {project.location}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Calendar size={14} />
                        <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif' }}>
                          {project.year}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button
              component={RouterLink}
              to="/portfolio"
              variant="outlined"
              size="large"
              endIcon={<ArrowRight />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 0,
                borderColor: '#333',
                color: '#333',
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: 1,
                fontFamily: '"Inter", sans-serif',
                '&:hover': {
                  borderColor: '#FF5722',
                  color: '#FF5722'
                }
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Team Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: '#999',
                fontWeight: 500,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Our Team
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                color: '#1A1A1A',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Meet the Experts
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              sx={{ 
                color: '#666',
                maxWidth: 700,
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Our passionate team of designers and architects brings decades of experience 
              to every project.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={member.name}>
                <Box 
                  sx={{ 
                    textAlign: 'center',
                    background: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    p: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 3,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '4px solid #FF5722'
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={getImageSrc(member.image)}
                      alt={member.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    sx={{ 
                      color: '#1A1A1A',
                      fontWeight: 600,
                      mb: 1,
                      fontFamily: '"Inter", sans-serif'
                    }}
                  >
                    {member.name}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#FF5722',
                      fontWeight: 500,
                      mb: 1,
                      fontFamily: '"Inter", sans-serif'
                    }}
                  >
                    {member.role}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#666',
                      fontSize: '0.9rem',
                      fontFamily: '"Inter", sans-serif'
                    }}
                  >
                    {member.experience}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: '#999',
                fontWeight: 500,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Client Testimonials
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                color: '#1A1A1A',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", sans-serif'
              }}
            >
              What Our Clients Say
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              sx={{ 
                color: '#666',
                maxWidth: 700,
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Don't just take our word for it - hear from clients who have experienced 
              our design excellence firsthand.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={testimonial.name}>
                <Card 
                  sx={{ 
                    height: '100%',
                    p: 3,
                    background: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    borderRadius: 0,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', mb: 3 }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} color="#FF5722" fill="#FF5722" />
                    ))}
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#1A1A1A',
                      mb: 4,
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      fontFamily: '"Inter", sans-serif'
                    }}
                  >
                    "{testimonial.content}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        mr: 2,
                        border: '2px solid #FF5722'
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={getImageSrc(testimonial.image)}
                        alt={testimonial.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#1A1A1A',
                          fontWeight: 600,
                          fontFamily: '"Inter", sans-serif'
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontFamily: '"Inter", sans-serif'
                        }}
                      >
                        {testimonial.role} • {testimonial.project}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Ready to Transform Your Space?
            </Typography>
            <Typography 
              variant="h5" 
              component="p" 
              sx={{ 
                mb: 6,
                opacity: 0.9,
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Let's bring your vision to life with our expert design services.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowRight />}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 0,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: 1,
                  fontFamily: '"Inter", sans-serif',
                  '&:hover': { 
                    background: 'rgba(255,255,255,0.3)'
                  }
                }}
              >
                Get In Touch
              </Button>
              <Button
                component={RouterLink}
                to="/services"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 0,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: 1,
                  fontFamily: '"Inter", sans-serif',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Our Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;